import { useEffect, useRef, type RefObject } from "react";
import confetti from "canvas-confetti";

import { useMediaQuery } from "sj-ui-toolkit/src/hooks";

const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";

interface UseFlagCelebrationArgs {
  enabled: boolean;
  captured: boolean;
  containerRef: RefObject<HTMLElement>;
}

/**
 * Fires a single confetti burst from the Submit flag button the first time a CTF flag is
 * accepted. Skipped entirely under prefers-reduced-motion and when the flag was already
 * accepted before mount.
 */
export function useFlagCelebration({ enabled, captured, containerRef }: UseFlagCelebrationArgs) {
  const prefersReducedMotion = useMediaQuery(REDUCED_MOTION_QUERY);
  // Seeded with the mount value so an already-captured flag never fires; set on the first
  // false -> true transition so it fires exactly once.
  const celebratedRef = useRef(captured);

  useEffect(() => {
    if (!enabled || !captured || celebratedRef.current) return;
    // Mark before the reduced-motion check so toggling the setting later can't fire a late burst.
    celebratedRef.current = true;
    if (prefersReducedMotion) return;

    const button = containerRef.current?.querySelector<HTMLElement>(
      '[data-testid="sandbox-submit-flag-button"]'
    );
    const rect = button?.getBoundingClientRect();
    const origin = rect
      ? {
          x: (rect.left + rect.width / 2) / document.documentElement.clientWidth,
          y: (rect.top + rect.height / 2) / document.documentElement.clientHeight,
        }
      : { x: 0.5, y: 0.25 };

    void confetti({
      particleCount: 120,
      spread: 70,
      startVelocity: 30,
      ticks: 180,
      scalar: 0.9,
      origin,
      zIndex: 1150, // Above the PrimeReact modal mask (1100) while the flag dialog fades out, below toasts (1200).
      disableForReducedMotion: true,
    });
  }, [enabled, captured, prefersReducedMotion, containerRef]);

  // Clear in-flight particles if the lesson unmounts mid-burst.
  useEffect(
    () => () => {
      confetti.reset();
    },
    []
  );
}
